import { useEffect, useState } from 'react';

const HORIZONTAL_GAP = 360;
const VERTICAL_GAP = 150;
const MAX_COLUMNS = 100;
const MAX_ROWS = 100;

const getSafeUserValue = (value) => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return '';
};

const getTimestamp = () => {
  try {
    if (
      typeof Intl === 'undefined' ||
      typeof Intl.DateTimeFormat !== 'function'
    ) {
      return '';
    }

    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Asia/Kolkata',
    }).format(new Date());
  } catch (error) {
    return '';
  }
};

export default function WatermarkLayer({ userEmail, userId } = {}) {
  const [watermarks, setWatermarks] = useState([]);
  const [stamp, setStamp] = useState('');

  const email = getSafeUserValue(userEmail);
  const id = getSafeUserValue(userId);
  const userIdentifier = email || id;

  useEffect(() => {
    // Covers SSR and non-browser test environments.
    if (typeof window === 'undefined') {
      return undefined;
    }

    const update = () => {
      try {
        const viewportWidth = Number(window.innerWidth) || 0;
        const viewportHeight = Number(window.innerHeight) || 0;

        const cols = Math.min(
          Math.max(Math.ceil(viewportWidth / HORIZONTAL_GAP) + 1, 1),
          MAX_COLUMNS,
        );

        const rows = Math.min(
          Math.max(Math.ceil(viewportHeight / VERTICAL_GAP) + 2, 1),
          MAX_ROWS,
        );

        const positions = Array.from({ length: cols * rows }, (_, index) => ({
          x: (index % cols) * HORIZONTAL_GAP - 180,
          y: Math.floor(index / cols) * VERTICAL_GAP - 100,
        }));

        setWatermarks(positions);
        setStamp(getTimestamp());
      } catch (error) {
        // Never allow watermark failures to affect the application.
        setWatermarks([]);
        setStamp('');
      }
    };

    update();

    window.addEventListener('resize', update);
    const interval = window.setInterval(update, 60000);

    return () => {
      window.removeEventListener('resize', update);
      window.clearInterval(interval);
    };
  }, []);

  // Do not render an empty or anonymous watermark.
  if (!userIdentifier) {
    return null;
  }

  const text = stamp ? `${userIdentifier}, ${stamp}` : userIdentifier;

  return (
    <div
      aria-hidden='true'
      className='tw-fixed tw-inset-0 tw-z-[2147483647] tw-pointer-events-none tw-select-none tw-overflow-hidden'
    >
      {watermarks.map(({ x, y }, index) => (
        <span
          key={`${x}-${y}-${index}`}
          className='tw-absolute tw-whitespace-nowrap tw-text-base tw-font-normal tw-tracking-wide tw-text-black/10'
          style={{
            left: x,
            top: y,
            transform: 'rotate(-30deg)',
          }}
        >
          {text}
        </span>
      ))}
    </div>
  );
}
